import { AgentReferenceType, ClientAgentReference } from '../store/schemas'

type ReferenceMetadataSummaryProps = {
  reference: ClientAgentReference
}

/**
 * One-line metadata summary for a reference, by type:
 * - FILE_UPLOAD_CHUNK: Shows legacy zero-based page numbers as one-based display pages
 * - USER_FILE_QDRANT_CHUNK: Shows Qdrant one-based page numbers as-is
 * - ASSET_EVENT: Shows asset-event zero-based page numbers as one-based display pages
 * - CASELAW: Shows jurisdiction, date, and docket
 * - LRR: Shows jurisdiction and law type
 * - WRITING_SAMPLE: Shows court, jurisdiction, and filed year
 * - FILE_UPLOAD_FULL: Shows nothing (full document)
 */
export default function ReferenceMetadataSummary(props: ReferenceMetadataSummaryProps) {
  const { reference } = props

  switch (reference.type) {
    case AgentReferenceType.FILE_UPLOAD_CHUNK:
    case AgentReferenceType.ASSET_EVENT: {
      const page_nums: number[] = reference.file_reference?.page_nums?.length ? reference.file_reference.page_nums : reference.metadata.page_nums
      const page_index_from_one = page_nums.map((page_num) => page_num + 1)

      switch (page_index_from_one.length) {
        case 0:
          return null
        case 1:
          return <div>Page {page_index_from_one[0]}</div>
        default:
          return <div>Pages {page_index_from_one.join(', ')}</div>
      }
    }

    case AgentReferenceType.USER_FILE_QDRANT_CHUNK: {
      const page_nums: number[] = reference.file_reference?.page_nums?.length ? reference.file_reference.page_nums : reference.metadata.page_nums

      switch (page_nums.length) {
        case 0:
          return null
        case 1:
          return <div>Page {page_nums[0]}</div>
        default:
          return <div>Pages {page_nums.join(', ')}</div>
      }
    }

    case AgentReferenceType.CASELAW: {
      const parts: string[] = []

      // Add jurisdiction if available
      if (reference.metadata.jurisdiction_code) {
        parts.push(reference.metadata.jurisdiction_code)
      }

      // Add date if available
      if (reference.metadata.decide_date) {
        try {
          const date = new Date(reference.metadata.decide_date)
          const year = date.getFullYear()
          if (!isNaN(year)) {
            parts.push(year.toString())
          }
        } catch {
          // Skip invalid dates
          console.error('Invalid date:', reference.metadata.decide_date)
        }
      }

      // Add docket if available
      if (reference.metadata.docket) {
        parts.push(`Docket ${reference.metadata.docket}`)
      }

      return parts.length > 0 ? <div>{parts.join(' • ')}</div> : null
    }

    case AgentReferenceType.LRR: {
      const parts: string[] = []

      // Add law type if available (capitalize first letter)
      if (reference.metadata.law_type) {
        const lawType = reference.metadata.law_type.charAt(0).toUpperCase() + reference.metadata.law_type.slice(1)
        parts.push(lawType)
      }

      // Add jurisdiction if available
      if (reference.metadata.jurisdiction_code) {
        parts.push(reference.metadata.jurisdiction_code)
      }

      return parts.length > 0 ? <div>{parts.join(' • ')}</div> : null
    }

    case AgentReferenceType.FILE_UPLOAD_FULL: {
      // Full document - no page numbers to display
      return null
    }

    case AgentReferenceType.TRELLIS_WRITING_SAMPLE: {
      const parts: string[] = ['Writing sample']
      if (reference.metadata.court) {
        parts.push(reference.metadata.court)
      }
      if (reference.metadata.jurisdiction) {
        parts.push(reference.metadata.jurisdiction)
      }
      if (reference.metadata.filed_date) {
        const year = new Date(reference.metadata.filed_date).getFullYear()
        if (!Number.isNaN(year)) {
          parts.push(String(year))
        }
      }
      return <div>{parts.join(' • ')}</div>
    }

    default:
      return null
  }
}
